import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import { useListData, usePostData } from "@utils/query/index.query";
import { endpoint } from "@services/api.services";
import { IndividualQuery } from "@utils/interface/index.interface";
import { CourseData } from "@utils/interface/journey.interface";
import { useEditorJS } from "@/editor/hooks/useEditorJs";
import { getCustomEditorTools } from "@/editor/utils/editorjs-tools";
import { Button } from "@components/ui/button";
import { Spinner } from "@components/ui/spinner";
import { showToast } from "@utils/helper/index.helper";
import { METHODS } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { AsyncSelect } from "@ui/internal/components/formelement";
import { Controller, useForm } from "react-hook-form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/ui/tabs";
import { X, Save } from "lucide-react";
import { cn } from "@/lib/utils";
import TestimonialContainer from "@ui/internal/components/course-drag/TestimonialContainer";
import FaqContainer from "@ui/internal/components/course-drag/FaqContainer";
import SkillsContainer from "@ui/internal/components/course-drag/SkillsContainer";
import CTAContainer from "@ui/internal/components/course-drag/CTAContainer";
import AudienceContainer from "@ui/internal/components/course-drag/AudienceContainer";
import SchemasField from "@/components/json-ld-schemas/SchemasField";
import {
  getJsonLdDefaults,
  validateJsonLdSchemas,
} from "@/components/json-ld-schemas/utils";
import "@/editor/utils/editorjs-style.scss";
import { baseURL } from "@/services/index.services";
import Cookies from "js-cookie";
import { IconName } from "@components/ui/icon-picker";

const TABS = [
  { id: "course_details", label: "Course Details", type: "editor" },
  { id: "skills", label: "Skills You'll Learn", type: "custom" },
  { id: "syllabus", label: "Course Syllabus", type: "editor" },
  // { id: "why_us", label: "Why Us", type: "editor" },
  // { id: "mentors", label: "Mentors", type: "mentor_select" },
  { id: "career_support", label: "Career Support", type: "editor" },
  { id: "cta", label: "CTA", type: "custom" },
  { id: "audience", label: "Audience", type: "custom" },
  { id: "testimonial", label: "Testimonials", type: "custom" },
  { id: "faq", label: "FAQs", type: "custom" },
  { id: "json_ld", label: "SEO Schemas", type: "custom" },
];

const CourseConfigureHub = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const courseId = searchParams.get("configure-course");
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("course_details");

  const { data: course, isPending } = useListData<IndividualQuery<CourseData>>({
    endpoint: endpoint["course"],
    id: courseId || "",
    key: "course-config-" + courseId,
    enabled: !!courseId,
  });

  const { mutate: updateCourse, isPending: isUpdating } = usePostData();

  // State to hold EditorJS data for all tabs
  const [configData, setConfigData] = useState<Record<string, any>>({});

  // Mentor and Custom fields form handling
  const {
    control,
    reset,
    watch: watchField,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<{
    mentors: { label: string; value: number }[];
    student_testimonial: any[];
    faqs: any[];
    skills: {
      eyebrow: string;
      heading: string;
      values: { icon?: string; title: string; description: string }[];
    };
    join_cta: { title: string; description: string; image: string }[];
    audience: {
      eyebrow: string;
      heading: string;
      values: { title: string; description: string; icon?: string }[];
    };
    json_ld: {
      id?: string | number;
      name?: string | null;
      content: string;
      order?: number;
    }[];
  }>({
    defaultValues: {
      mentors: [],
      student_testimonial: [],
      faqs: [],
      skills: { eyebrow: "", heading: "", values: [] },
      join_cta: [],
      audience: { eyebrow: "", heading: "", values: [] },
      json_ld: [],
    },
  });

  useEffect(() => {
    if (course?.data) {
      setConfigData({
        course_details: { blocks: course.data.course_details || [] },
        syllabus: { blocks: course.data.syllabus || [] },
        career_support: { blocks: course.data.career_support || [] },
      });
      reset({
        mentors: course.data.mentor
          ? course.data.mentor.map((m) => ({ label: m.full_name, value: m.id }))
          : [],
        student_testimonial: course.data.student_testimonial || [],
        faqs: course.data.faqs || [],
        skills: Array.isArray(course.data.skills)
          ? { eyebrow: "", heading: "", values: course.data.skills }
          : course.data.skills || { eyebrow: "", heading: "", values: [] },
        join_cta: course.data.join_cta || [],
        audience: course.data.audience || { eyebrow: "", heading: "", values: [] },
        json_ld: getJsonLdDefaults((course.data as any)?.json_ld),
      });
    }
  }, [course]);

  const handleClose = () => {
    searchParams.delete("configure-course");
    setSearchParams(searchParams);
  };

  const handleSave = async () => {
    if (!courseId) return;

    // Validate every json_ld block parses; block save + surface inline errors if any fail.
    clearErrors("json_ld");
    const jsonLdResult = validateJsonLdSchemas(
      getValues("json_ld") || [],
      setError as any,
    );
    if (!jsonLdResult.isValid) {
      setActiveTab("json_ld");
      showToast({
        title: "Invalid JSON-LD",
        description: `Fix the highlighted schema block (#${jsonLdResult.firstInvalidIndex + 1}) before saving.`,
        type: "error",
      });
      return;
    }
    console.log("CourseConfigureHub payload", {
      data: {
        id: courseId,
        course_details: configData["course_details"]?.blocks || [],
        syllabus: configData["syllabus"]?.blocks || [],
        career_support: configData["career_support"]?.blocks || [],
        student_testimonial: getValues("student_testimonial"),
        faqs: getValues("faqs"),
        skills: getValues("skills"),
        join_cta: getValues("join_cta"),
        audience: getValues("audience"),
        json_ld: jsonLdResult.normalizedSchemas,
      },
      endpoint: endpoint["course"],
      method: METHODS.PATCH,
    });

    updateCourse(
      {
        data: {
          id: courseId,
          course_details: configData["course_details"]?.blocks || [],
          syllabus: configData["syllabus"]?.blocks || [],
          career_support: configData["career_support"]?.blocks || [],
          student_testimonial: getValues("student_testimonial"),
          faqs: getValues("faqs"),
          skills: getValues("skills"),
          join_cta: getValues("join_cta"),
          audience: getValues("audience"),
          json_ld: jsonLdResult.normalizedSchemas,
        },
        endpoint: endpoint["course"],
        method: METHODS.PATCH,
      },
      {
        onSuccess: () => {
          showToast({
            title: "Success",
            description: "Course configuration saved",
            type: "success",
          });
          queryClient.invalidateQueries({ queryKey: ["course" + courseId] });
          queryClient.invalidateQueries({ queryKey: ["course-listing"] });
        },
      },
    );
  };

  if (!courseId) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-white flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-300">
      {/* Header */}
      <div className="px-6 py-4 border-b flex items-center justify-between bg-slate-50">
        <div>
          <h1 className="text-xl font-bold text-slate-800">
            Configure {isPending ? "Course..." : course?.data?.title}
          </h1>
          <p className="text-sm text-slate-500">
            Edit course sections and dynamic content
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            className="flex items-center gap-2"
          >
            <X size={18} /> Close
          </Button>
          <Button
            onClick={handleSave}
            disabled={isUpdating}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
          >
            {isUpdating ? <Spinner size="sm" /> : <Save size={18} />}
            Save Configuration
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {isPending || Object.keys(configData).length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <Spinner size="lg" />
          </div>
        ) : (
          <Tabs
            defaultValue="overview"
            className="flex-1 flex flex-col min-h-0"
            value={activeTab}
            onValueChange={setActiveTab}
          >
            <div className="flex-1 flex min-h-0 overflow-hidden">
              {/* Tab List (Sidebar) */}
              <div className="w-64 border-r bg-slate-50 p-4 overflow-y-auto h-full">
                <TabsList className="flex flex-col w-full h-auto bg-transparent gap-2">
                  {TABS.map((tab) => (
                    <TabsTrigger
                      key={tab.id}
                      value={tab.id}
                      className={cn(
                        "w-full justify-start px-4 py-3 text-left font-medium transition-all rounded-lg cursor-pointer",
                        "data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-emerald-700",
                      )}
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>

              {/* Tab Content (Editor Area) */}
              <div className=" flex-1 overflow-y-auto h-full p-8 bg-slate-100/30">
                {TABS.map((tab) => (
                  <TabsContent
                    key={tab.id}
                    value={tab.id}
                    className="mt-0 focus-visible:outline-none"
                  >
                    <div className="mx-auto bg-white rounded-xl shadow-sm border p-8 min-h-[600px]">
                      <h2 className="text-2xl font-bold mb-6 text-slate-800 border-b pb-4">
                        {tab.label}
                      </h2>

                      {tab.type === "editor" ? (
                        <EditorTabContent
                          tabId={tab.id}
                          initialData={configData[tab.id]}
                          onChange={(data) => {
                            setConfigData((prev) => ({
                              ...prev,
                              [tab.id]: data,
                            }));
                          }}
                        />
                      ) : tab.id === "testimonial" ? (
                        <TestimonialContainer control={control} />
                      ) : tab.id === "faq" ? (
                        <FaqContainer control={control} />
                      ) : tab.id === "skills" ? (
                        <SkillsContainer control={control} />
                      ) : tab.id === "cta" ? (
                        <CTAContainer control={control} />
                      ) : tab.id === "json_ld" ? (
                        <SchemasField
                          control={control}
                          errors={errors}
                          name="json_ld"
                        />
                      ) : tab.id === "audience" ? (
                        <AudienceContainer control={control} />
                      ) : (
                        <div className="space-y-4">
                          <label className="block text-sm font-semibold text-slate-700">
                            Select Mentors for this course
                          </label>
                          <Controller
                            name="mentors"
                            control={control}
                            render={({ field }) => (
                              <AsyncSelect
                                {...field}
                                endpoint={endpoint["mentor"]}
                                format={{
                                  key: "full_name",
                                  value: "id",
                                  mutate: "name",
                                }}
                                isMulti
                                placeholder="Search and select mentors..."
                                className="w-full"
                              />
                            )}
                          />
                        </div>
                      )}
                    </div>
                  </TabsContent>
                ))}
              </div>
            </div>
          </Tabs>
        )}
      </div>
    </div>
  );
};

interface EditorTabContentProps {
  tabId: string;
  initialData?: any;
  onChange: (data: any) => void;
}

const EditorTabContent = ({
  tabId,
  initialData,
  onChange,
}: EditorTabContentProps) => {
  const imageUploadFn = async (file: File) => {
    const formData = new FormData();

    // Pasted files often don't have a name, which backend might require.
    // We create a new File object with a timestamped name if necessary.
    const fileName =
      file.name && file.name !== "image.png"
        ? file.name
        : `pasted-${Date.now()}.${file.type.split("/")[1] || "png"}`;

    const finalFile = new File([file], fileName, { type: file.type });
    formData.append("image", finalFile);

    const response = await fetch(`${baseURL}/custom-gallery/`, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${
          sessionStorage.getItem("access") || Cookies.get("access")
        }`,
      },
    });
    const result = await response.json();
    if (response.ok) {
      return { url: result.image };
    } else {
      throw new Error(result.error?.message || "Image upload failed");
    }
  };

  const { holderRef, isReady, editor } = useEditorJS({
    data: initialData,
    placeholder: `Write content for ${tabId}...`,
    tools: getCustomEditorTools(imageUploadFn, {
      faqEnabled: true,
      accordianEnabled: true,
    }) as any,
    onChange: (data) => {
      onChange(data);
    },
  });

  return (
    <div className="prose max-w-none editor-style">
      <div ref={holderRef as any} className="editorjs-container" />
      {!isReady && (
        <div className="flex items-center justify-center p-12">
          <Spinner />
        </div>
      )}
    </div>
  );
};

export default CourseConfigureHub;
